import {
    REVIEWABLE,
    STAGE_GROUP_ORDER,
    formatShortDate,
    formatShortDateTime,
} from './reviewerStages';

/**
 * How one position's candidate list is filtered, ordered and summed up. Pure
 * functions: the server decides what each reviewer may see and do, and these
 * only arrange what it sent.
 */

export const OFFER_STAGES = Object.freeze(['offered', 'expired']);

// Stages that end a candidacy without a place; shown under "Show finished".
// `unavailable` is how faculty receive every closure that was not the
// student's own.
export const FINISHED_STAGES = Object.freeze([
    'released',
    'declined',
    'not_selected',
    'withdrawn',
    'closed',
    'unavailable',
]);

export const FILTERS = Object.freeze([
    { key: 'review', label: 'Needs review' },
    { key: 'offers', label: 'Offers out' },
    { key: 'all', label: 'All' },
]);

const FILTER_KEYS = FILTERS.map((f) => f.key);

export const isFilter = (value) => FILTER_KEYS.includes(value);

const isFinished = (c) => FINISHED_STAGES.includes(c.stage);

const inFilter = (filter, c) => {
    if (c.stage === 'accepted') return false;
    if (filter === 'review') return REVIEWABLE.includes(c.stage);
    if (filter === 'offers') return OFFER_STAGES.includes(c.stage);
    return true;
};

const rankOf = (c) =>
    Number.isInteger(c.positionRank)
        ? c.positionRank
        : Number.POSITIVE_INFINITY;

const stageIndex = (stage) => {
    const index = STAGE_GROUP_ORDER.indexOf(stage);
    return index === -1 ? STAGE_GROUP_ORDER.length : index;
};

// Furthest along first, then by the reviewers' rank inside a stage. The sort
// is stable, so the server's order is kept where neither decides.
export function ordered(candidacies) {
    return [...candidacies].sort(
        (a, b) =>
            stageIndex(a.stage) - stageIndex(b.stage) || rankOf(a) - rankOf(b)
    );
}

export const matchesSearch = (c, query) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return true;
    return (c.student?.name || '').toLowerCase().includes(q);
};

export const countFor = (candidacies, filter) =>
    candidacies.filter((c) => inFilter(filter, c)).length;

/**
 * The rows a filter and a search show, split into the active ones and the
 * finished ones (collapsed by default). Accepted students are listed apart,
 * as the team.
 */
export function visibleRows(candidacies, { filter, query }) {
    const rows = ordered(candidacies).filter(
        (c) => inFilter(filter, c) && matchesSearch(c, query)
    );
    return {
        active: rows.filter((c) => !isFinished(c)),
        finished: rows.filter(isFinished),
    };
}

/**
 * The filter a list opens on: what needs review, else offers awaiting an
 * answer, else everyone. A candidate opened by its address is always in the
 * list it opens on.
 */
export function defaultFilter(candidacies, selectedId) {
    const selected = candidacies.find((c) => c.id === selectedId);
    if (selected && selected.stage !== 'accepted') {
        return FILTERS.find((f) => inFilter(f.key, selected)).key;
    }
    if (countFor(candidacies, 'review') > 0) return 'review';
    if (countFor(candidacies, 'offers') > 0) return 'offers';
    return 'all';
}

const returningText = (c) => {
    const returning = c.profile?.laDetails?.isReturning;
    if (returning === true) return 'Returning LA';
    if (returning === false) return 'New LA';
    return null;
};

/** The one fact a list row shows under the name. */
export function secondaryLine(c) {
    const { interview, offer } = c;
    if (c.stage === 'offered' && offer?.respondBy) {
        return `Answer by ${formatShortDate(offer.respondBy)}`;
    }
    if (c.stage === 'expired' && offer?.respondBy) {
        return `Offer expired ${formatShortDate(offer.respondBy)}`;
    }
    if (interview?.when && REVIEWABLE.includes(c.stage)) {
        return `Interview ${formatShortDateTime(interview.when)}`;
    }
    const applied = c.createdAt
        ? `Applied ${formatShortDate(c.createdAt)}`
        : '';
    return [returningText(c), applied].filter(Boolean).join(', ');
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * The section's places in words, from the position's counts and the
 * candidates: "3 places awarded: 1 accepted, 1 awaiting an answer,
 * 1 available." An expired offer still holds its place, so it is named apart
 * rather than counted as awaiting an answer.
 */
export function staffingText(position, candidacies, nominations = []) {
    if (!position) return { sentence: '', expired: [] };
    if (position.status === 'pending') {
        return {
            sentence:
                'This award is not final yet, so its places cannot be filled.',
            expired: [],
        };
    }
    const expired = candidacies.filter((c) => c.stage === 'expired');
    const outstanding =
        (position.offersOutstanding || 0) - expired.length > 0
            ? (position.offersOutstanding || 0) - expired.length
            : 0;
    const awaiting =
        outstanding +
        (position.nomineeHeld ??
            nominations.filter((n) => n.status === 'offered').length);
    const parts = [
        `${position.filled || 0} accepted`,
        `${awaiting} awaiting an answer`,
        `${position.openSlots || 0} available`,
    ];
    let sentence = `${plural(position.awarded || 0, 'place', 'places')} awarded: ${parts.join(', ')}.`;
    if (position.closedByDecline > 0) {
        sentence += ` ${plural(position.closedByDecline, 'place', 'places')} closed when a named student declined.`;
    }
    return { sentence, expired };
}

/**
 * What needs the reviewer in this section now: students to review (and how
 * many are new since the last visit), and offers waiting for an answer with
 * the first deadline.
 */
export function needsYou(candidacies, since) {
    const review = candidacies.filter((c) => REVIEWABLE.includes(c.stage));
    const fresh = since
        ? review.filter(
              (c) => c.createdAt && new Date(c.createdAt) > new Date(since)
          )
        : [];
    const offers = candidacies.filter((c) => c.stage === 'offered');
    const firstDue = offers
        .map((c) => c.offer?.respondBy)
        .filter(Boolean)
        .sort((a, b) => new Date(a) - new Date(b))[0];
    return {
        review: review.length,
        fresh: new Set(fresh.map((c) => c.id)),
        offers: offers.length,
        firstDue: firstDue || null,
    };
}

const visitKey = (program, applicationId) =>
    `psh.hiring.lastVisit.${program}.${applicationId}`;

/**
 * The time this browser last opened the section, and a record of now as the
 * next visit's answer. Null on a first visit, so nothing reads as new then.
 */
export function takeLastVisit(program, applicationId, now = new Date()) {
    try {
        const key = visitKey(program, applicationId);
        const before = window.localStorage.getItem(key);
        window.localStorage.setItem(key, now.toISOString());
        return before;
    } catch (_err) {
        return null;
    }
}
